import { cliente } from '@/lib/cliente';
import type {
  Actividad,
  ActualizarActividadRequest,
  ActualizarEnsayoRequest,
  ActualizarMedicionRequest,
  ActualizarMuestraRequest,
  CrearActividadRequest,
  CrearEnsayoRequest,
  CrearMedicionRequest,
  CrearMuestraRequest,
  Ensayo,
  FotoMuestra,
  ImportarExcelLabResponse,
  ListaFotosMuestraResponse,
  ListaMuestrasResponse,
  ListarMuestrasRequest,
  Medicion,
  MuestraDetalle,
  PuntoCurvaTeorica,
  RespuestaApi,
} from '@adelante/shared';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// =============================================================================
// Helpers de caché optimista del detalle de muestra
// =============================================================================
//
// La API (Azure Functions) puede tener latencia por cold starts, así que en
// vez de depender solo de invalidateQueries → refetch (que tarda y se cancela
// al navegar, dejando la caché vieja) actualizamos la caché del detalle
// directamente para que la UI responda al instante. La invalidación se deja
// como reconciliación eventual.

const FACTOR_MPA_A_KGCM2 = 10.197;

/**
 * Query key del detalle de una muestra. CRÍTICO: coercionamos a número.
 * `lab.muestras.id` es BIGINT y el driver mssql lo devuelve como string
 * (`muestra.id === "247"`), pero `useMuestra` arma la key con el número del
 * route param (`Number("247") === 247`). Si las mutaciones keyean con el
 * string, `setQueryData`/`invalidateQueries` apuntan a una entrada fantasma
 * y el detalle montado nunca se refresca (había que salir y volver a entrar).
 */
const keyMuestra = (id: number | string) => ['lab', 'muestra', Number(id)] as const;

/** Recalcula los agregados (promedio MPa / kg-cm²) de un ensayo. */
function recalcularEnsayo(e: Ensayo): Ensayo {
  const meds = e.mediciones ?? [];
  const n = meds.length;
  const prom = n > 0 ? meds.reduce((s, m) => s + m.resistencia_mpa, 0) / n : null;
  return {
    ...e,
    cantidad_mediciones: n,
    resistencia_mpa_promedio: prom,
    resistencia_kg_cm2_promedio: prom === null ? null : prom * FACTOR_MPA_A_KGCM2,
  };
}

/** Aplica `fn` al MuestraDetalle cacheado de `idMuestra` (si existe). */
function editarDetalleEnCache(
  qc: QueryClient,
  idMuestra: number | string,
  fn: (d: MuestraDetalle) => MuestraDetalle,
) {
  qc.setQueryData<RespuestaApi<MuestraDetalle>>(keyMuestra(idMuestra), (old) =>
    old?.data ? { ...old, data: fn(old.data) } : old,
  );
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Catálogo de actividades (Columna/Viga/Losa/...). Cache largo: se modifica
 * con poca frecuencia desde un admin panel. `staleTime` 5 min reduce trash.
 */
export function useActividades(incluirInactivas = false) {
  return useQuery<RespuestaApi<{ actividades: Actividad[] }>>({
    queryKey: ['lab', 'actividades', incluirInactivas],
    queryFn: () => cliente.lab.listarActividades(incluirInactivas),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Curva teórica de resistencia (ASTM C-150). Tabla compartida que casi
 * nunca cambia → cacheamos para toda la sesión.
 */
export function useCurvaTeorica() {
  return useQuery<RespuestaApi<{ puntos: PuntoCurvaTeorica[] }>>({
    queryKey: ['lab', 'curva-teorica'],
    queryFn: () => cliente.lab.listarCurvaTeorica(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Listado de muestras con filtros. Key incluye todos los filtros. */
export function useMuestras(filtros: Partial<ListarMuestrasRequest>) {
  return useQuery<RespuestaApi<ListaMuestrasResponse>>({
    queryKey: ['lab', 'muestras', filtros],
    queryFn: () => cliente.lab.listarMuestras(filtros),
  });
}

/** Detalle de una muestra incluyendo sus ensayos + mediciones. */
export function useMuestra(id: number | null) {
  return useQuery<RespuestaApi<MuestraDetalle>>({
    queryKey: ['lab', 'muestra', id],
    queryFn: () => {
      if (id === null) throw new Error('id es null');
      return cliente.lab.obtenerMuestra(id);
    },
    enabled: id !== null,
  });
}

/**
 * Importación masiva del Excel histórico. Toast con resumen + invalida
 * el listado de muestras para que se vea reflejado inmediatamente.
 */
export function useImportarExcelLab() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<ImportarExcelLabResponse>, Error, File>({
    mutationFn: (archivo) => cliente.lab.importarExcel(archivo),
    onSuccess: (r) => {
      if (r.data) {
        const {
          muestras_insertadas,
          muestras_actualizadas,
          muestras_duplicadas,
          mediciones_insertadas,
          advertencias,
          errores,
        } = r.data;
        const hubo = muestras_insertadas + muestras_actualizadas > 0;
        if (hubo) {
          toast.success(`${muestras_insertadas} nuevas · ${muestras_actualizadas} actualizadas`, {
            description: `${mediciones_insertadas} resultados cargados · ${muestras_duplicadas} sin cambios · ${advertencias.length} avisos · ${errores.length} errores`,
          });
        } else if (muestras_duplicadas > 0) {
          toast.warning('Sin cambios', {
            description: `Las ${muestras_duplicadas} muestras del Excel ya estaban completas en BD.`,
          });
        } else {
          toast.warning('Nada para importar', {
            description: 'No se encontraron filas válidas en el Excel.',
          });
        }
        // Los avisos (typos de año, etc.) los mostramos aparte para que el
        // laboratorio los vea — pueden ser varios, así que logueamos a consola
        // y mostramos los primeros en un toast informativo.
        if (advertencias.length > 0) {
          console.warn('[import-lab] advertencias:', advertencias);
          toast.info(`${advertencias.length} aviso(s) en la importación`, {
            description: advertencias.slice(0, 3).join(' · '),
            duration: 10000,
          });
        }
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
        qc.invalidateQueries({ queryKey: ['lab', 'actividades'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido en importación');
      }
    },
    onError: (e) => {
      toast.error('Error al importar Excel', { description: e.message });
    },
  });
}

// =============================================================================
// Mutations — actividades
// =============================================================================

export function useCrearActividad() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<Actividad>, Error, CrearActividadRequest>({
    mutationFn: (body) => cliente.lab.crearActividad(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(`Actividad "${r.data.nombre}" creada`);
        qc.invalidateQueries({ queryKey: ['lab', 'actividades'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo crear la actividad', { description: e.message });
    },
  });
}

export function useActualizarActividad() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<Actividad>, Error, { id: number } & ActualizarActividadRequest>({
    mutationFn: ({ id, ...body }) => cliente.lab.actualizarActividad(id, body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success('Actividad actualizada');
        qc.invalidateQueries({ queryKey: ['lab', 'actividades'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar la actividad', { description: e.message });
    },
  });
}

// =============================================================================
// Mutations — muestras
// =============================================================================

export function useCrearMuestra() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<MuestraDetalle>, Error, CrearMuestraRequest>({
    mutationFn: (body) => cliente.lab.crearMuestra(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success(`Muestra #${r.data.numero_muestra} creada`);
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
        qc.setQueryData(keyMuestra(r.data.id), r);
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo crear la muestra', { description: e.message });
    },
  });
}

export function useActualizarMuestra() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<MuestraDetalle>,
    Error,
    { id: number } & ActualizarMuestraRequest
  >({
    mutationFn: ({ id, ...body }) => cliente.lab.actualizarMuestra(id, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Muestra actualizada');
        qc.setQueryData(keyMuestra(vars.id), r);
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar la muestra', { description: e.message });
    },
  });
}

export function useEliminarMuestra() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number }>({
    mutationFn: ({ id }) => cliente.lab.eliminarMuestra(id),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Muestra eliminada');
        qc.removeQueries({ queryKey: keyMuestra(vars.id) });
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo eliminar la muestra', { description: e.message });
    },
  });
}

// =============================================================================
// Mutations — ensayos
// =============================================================================

/**
 * Invalida el detalle de la muestra padre al éxito. Cada mutación de
 * ensayo/medición recibe el `idMuestra` para refrescar la vista correcta.
 */
export function useCrearEnsayo() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<Ensayo>, Error, { idMuestra: number } & CrearEnsayoRequest>({
    mutationFn: ({ idMuestra, ...body }) => cliente.lab.crearEnsayo(idMuestra, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        const nuevo = r.data;
        toast.success(`Ensayo de ${vars.edad_dias} días creado`);
        // Append directo a la caché del detalle para que la tarjeta del
        // ensayo aparezca al instante. La invalidación sola no siempre
        // re-renderizaba el listado (había que salir y volver a entrar).
        qc.setQueryData<RespuestaApi<MuestraDetalle>>(keyMuestra(vars.idMuestra), (old) => {
          if (!old?.data) return old;
          if (old.data.ensayos.some((e) => e.id === nuevo.id)) return old;
          return {
            ...old,
            data: {
              ...old.data,
              ensayos: [...old.data.ensayos, { ...nuevo, mediciones: nuevo.mediciones ?? [] }],
            },
          };
        });
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo crear el ensayo', { description: e.message });
    },
  });
}

export function useActualizarEnsayo() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<{ ok: true }>,
    Error,
    { id: number; idMuestra: number } & ActualizarEnsayoRequest
  >({
    mutationFn: ({ id, idMuestra: _, ...body }) => cliente.lab.actualizarEnsayo(id, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Ensayo actualizado');
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar el ensayo', { description: e.message });
    },
  });
}

export function useEliminarEnsayo() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number; idMuestra: number }>({
    mutationFn: ({ id }) => cliente.lab.eliminarEnsayo(id),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Ensayo eliminado');
        editarDetalleEnCache(qc, vars.idMuestra, (d) => ({
          ...d,
          ensayos: d.ensayos.filter((e) => e.id !== vars.id),
        }));
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo eliminar el ensayo', { description: e.message });
    },
  });
}

// =============================================================================
// Mutations — mediciones (probetas individuales)
// =============================================================================

export function useCrearMedicion() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<Medicion>,
    Error,
    { idEnsayo: number; idMuestra: number } & CrearMedicionRequest
  >({
    mutationFn: ({ idEnsayo, idMuestra: _, ...body }) => cliente.lab.crearMedicion(idEnsayo, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        const nueva = r.data;
        toast.success('Medición agregada');
        editarDetalleEnCache(qc, vars.idMuestra, (d) => ({
          ...d,
          ensayos: d.ensayos.map((e) =>
            e.id === vars.idEnsayo
              ? recalcularEnsayo({ ...e, mediciones: [...(e.mediciones ?? []), nueva] })
              : e,
          ),
        }));
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo agregar la medición', { description: e.message });
    },
  });
}

export function useActualizarMedicion() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<{ ok: true }>,
    Error,
    { id: number; idMuestra: number } & ActualizarMedicionRequest
  >({
    mutationFn: ({ id, idMuestra: _, ...body }) => cliente.lab.actualizarMedicion(id, body),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Medición actualizada');
        editarDetalleEnCache(qc, vars.idMuestra, (d) => ({
          ...d,
          ensayos: d.ensayos.map((e) =>
            (e.mediciones ?? []).some((m) => m.id === vars.id)
              ? recalcularEnsayo({
                  ...e,
                  mediciones: (e.mediciones ?? []).map((m) =>
                    m.id === vars.id
                      ? {
                          ...m,
                          resistencia_mpa: vars.resistencia_mpa ?? m.resistencia_mpa,
                          orden: vars.orden ?? m.orden,
                          notas: vars.notas !== undefined ? vars.notas : m.notas,
                        }
                      : m,
                  ),
                })
              : e,
          ),
        }));
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar la medición', { description: e.message });
    },
  });
}

export function useEliminarMedicion() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number; idMuestra: number }>({
    mutationFn: ({ id }) => cliente.lab.eliminarMedicion(id),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Medición eliminada');
        editarDetalleEnCache(qc, vars.idMuestra, (d) => ({
          ...d,
          ensayos: d.ensayos.map((e) =>
            (e.mediciones ?? []).some((m) => m.id === vars.id)
              ? recalcularEnsayo({
                  ...e,
                  mediciones: (e.mediciones ?? []).filter((m) => m.id !== vars.id),
                })
              : e,
          ),
        }));
        qc.invalidateQueries({ queryKey: keyMuestra(vars.idMuestra) });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo eliminar la medición', { description: e.message });
    },
  });
}

// =============================================================================
// Fotos de muestra
// =============================================================================

/**
 * Fotos de una muestra. Las URLs son SAS temporales (~2h), así que
 * refrescamos cada ~25 min para no servir URLs vencidas en sesiones largas.
 */
export function useFotosMuestra(idMuestra: number | string | null) {
  return useQuery<RespuestaApi<ListaFotosMuestraResponse>>({
    queryKey: ['lab', 'fotos', idMuestra === null ? null : Number(idMuestra)],
    queryFn: () => {
      if (idMuestra === null) throw new Error('idMuestra es null');
      return cliente.lab.listarFotos(Number(idMuestra));
    },
    enabled: idMuestra !== null,
    staleTime: 25 * 60 * 1000,
  });
}

export function useSubirFoto() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<FotoMuestra>,
    Error,
    { idMuestra: number | string; blob: Blob; nombre?: string; idEnsayo?: number | null }
  >({
    mutationFn: ({ idMuestra, blob, nombre, idEnsayo }) =>
      cliente.lab.subirFoto(Number(idMuestra), blob, { nombre, idEnsayo }),
    onSuccess: (r, vars) => {
      if (r.data) {
        qc.invalidateQueries({ queryKey: ['lab', 'fotos', Number(vars.idMuestra)] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error al subir la foto');
      }
    },
    onError: (e) => {
      toast.error('No se pudo subir la foto', { description: e.message });
    },
  });
}

/** Reasigna una foto a un ensayo distinto (o la deja como general). */
export function useActualizarFoto() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<FotoMuestra>,
    Error,
    { id: number; idMuestra: number | string; id_ensayo: number | null }
  >({
    mutationFn: ({ id, id_ensayo }) => cliente.lab.actualizarFoto(id, { id_ensayo }),
    onSuccess: (r, vars) => {
      if (r.data) {
        toast.success('Foto actualizada');
        qc.invalidateQueries({ queryKey: ['lab', 'fotos', Number(vars.idMuestra)] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error al actualizar la foto');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar la foto', { description: e.message });
    },
  });
}

export function useEliminarFoto() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<{ ok: true }>, Error, { id: number; idMuestra: number | string }>(
    {
      mutationFn: ({ id }) => cliente.lab.eliminarFoto(id),
      onSuccess: (r, vars) => {
        if (r.data) {
          toast.success('Foto eliminada');
          qc.invalidateQueries({ queryKey: ['lab', 'fotos', Number(vars.idMuestra)] });
        } else {
          toast.error(r.error?.mensaje ?? 'Error al eliminar la foto');
        }
      },
      onError: (e) => {
        toast.error('No se pudo eliminar la foto', { description: e.message });
      },
    },
  );
}
